import { Route } from 'react-router';
import { CatalogEntityPage, CatalogIndexPage } from '@backstage/plugin-catalog';
import { CatalogImportPage } from '@backstage/plugin-catalog-import';
import { ScaffolderPage } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { ApiExplorerPage } from '@backstage/plugin-api-docs';
import { ZeissTechRadarPage } from '@zeiss/plugin-zeiss-tech-radar';
import { ZeissGraphiqlPage } from '@zeiss/plugin-zeiss-graphiql';
import { HomePage } from './components/home/HomePage';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ExpandableNavigation,
  LightBox,
  ReportIssue,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  SettingsLayout,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { CustomCatalogPage } from './components/catalog/catalog-page/CustomCatalogPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { TokenRetrievalPage } from '@zeiss/plugin-token-retrieval';
import { RBACRoot } from '@spotify/backstage-plugin-rbac';
import { SoundcheckRoutingPage } from '@spotify/backstage-plugin-soundcheck';
import { QetaPage } from '@drodil/backstage-plugin-qeta';
import { VersionDashboardPage } from '@zeiss/plugin-version-dashboard';
import {
  BeyondDesignSystemRouterPath,
  DesignSystemPage,
} from '@zeiss/backstage-plugin-design-system';
import {
  NotificationsPage,
  UserNotificationSettingsCard,
} from '@backstage/plugin-notifications';

import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import {
  frontendGraphiqlPermission,
  frontendSkillExchangePermission,
  frontendTokenPermission,
  frontendVersionDashboardPermission,
} from '@zeiss/backstage-plugin-frontend-permissions-common';

import { beyondTheme } from './theme';
import { UnifiedThemeProvider } from '@backstage/theme';
import * as plugins from './plugins';
import { VisitListener } from '@backstage/plugin-home';
import { EntityLinterPage } from '@zeiss/plugin-entity-linter';
import { ZeissLighthousePage } from '@zeiss/plugin-zeiss-lighthouse';
import {
  ZeissFeatureFlagsProvider,
  ZeissFeatureFlagged,
} from '@zeiss/backstage-plugin-feature-flags';

import {
  DescribedSelectionFieldExtension,
  MultiEntityPickerExtension,
} from './scaffolder';
import { SelectFieldFromApiExtension } from '@roadiehq/plugin-scaffolder-frontend-module-http-request-field';
import {
  withRoutablePermission,
  withRoutableWrapper,
} from './routing/withRoutablePermission';
import { CreateNotificationComponent } from './components/catalog/components/CreateNotificationComponent/CreateNotificationComponent';
import {
  InsightsPage,
  InsightsSurveyLoader,
} from '@spotify/backstage-plugin-insights';
import { UpdateCheckAnalysisPage } from '@zeiss/plugin-update-check';
import { SkillExchangePage } from '@spotify/backstage-plugin-skill-exchange';
import { GenaiPlatformFrontendPage } from '@zeiss/backstage-plugin-genai-platform';
import { DigitalEcosystemEnablementStandardsPage } from '@zeiss/plugin-digital-ecosystem-enablement-standards';
import './styles.css';

import rehypeMermaid from 'rehype-mermaid';

// @ts-ignore (necessary because plugin lacks type definitions)
import simplePlantUML from '@akebifiky/remark-simple-plantuml';

import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';

const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  plugins: Object.values(plugins),
  components: {
    SignInPage: props => {
      const config = useApi(configApiRef);
      // Customize the SignInPage based on the environment
      const isE2eTest = config.getString('auth.environment') === 'e2e_test';
      const provider = isE2eTest
        ? 'custom'
        : {
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in Azure Active Directory',
            apiRef: microsoftAuthApiRef,
          };
      return <SignInPage {...props} auto providers={[provider]} />;
    },
  },
  themes: [
    {
      id: 'beyond-theme',
      title: 'Beyond Online Theme',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={beyondTheme} children={children} />
      ),
    },
  ],
  featureFlags: [
    {
      pluginId: '',
      name: 'api-spectral-linter',
      description: 'Enables the Spectral Linter for APIs',
    },
    {
      pluginId: '',
      name: 'lighthouse',
      description: 'Enables the Lighthouse audit UI',
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>

    <Route
      path="/catalog/:namespace/:kind/:name/create-notification"
      element={<CreateNotificationComponent />}
    />

    <Route
      path="/docs"
      element={<TechDocsIndexPage pagination={{ mode: 'offset' }} />}
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <ExpandableNavigation />
        <LightBox />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <MultiEntityPickerExtension />
        <DescribedSelectionFieldExtension />
        <SelectFieldFromApiExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route
      path="/api-docs"
      element={<ApiExplorerPage pagination={{ mode: 'offset' }} />}
    />
    <Route path="/catalog-import" element={<CatalogImportPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/token-retrieval"
      element={withRoutablePermission(
        <TokenRetrievalPage />,
        frontendTokenPermission,
      )}
    />
    <Route path="/tech-radar" element={<ZeissTechRadarPage />} />
    <Route path="/devtools" element={<DevToolsPage />}>
      {customDevToolsPage}
    </Route>
    <Route
      path="/graphiql"
      element={withRoutablePermission(
        <ZeissGraphiqlPage />,
        frontendGraphiqlPermission,
      )}
    />
    <Route path="/rbac" element={<RBACRoot />} />
    <Route path="/soundcheck" element={<SoundcheckRoutingPage />} />
    <Route
      path="/qeta"
      element={
        <QetaPage
          title="Community"
          rehypePlugins={[rehypeMermaid]}
          remarkPlugins={[simplePlantUML]}
        />
      }
    />
    <Route
      path="/version-dashboard"
      element={withRoutablePermission(
        <VersionDashboardPage />,
        frontendVersionDashboardPermission,
      )}
    />
    <Route path="/entity-linter" element={<EntityLinterPage />} />
    <Route
      path="/lighthouse/*"
      element={withRoutableWrapper(<ZeissLighthousePage />, page => (
        <ZeissFeatureFlagged name="lighthouse">{page}</ZeissFeatureFlagged>
      ))}
    />
    <Route path={BeyondDesignSystemRouterPath} element={<DesignSystemPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/insights" element={<InsightsPage />} />
    <Route
      path="/skill-exchange"
      element={withRoutablePermission(
        <SkillExchangePage />,
        frontendSkillExchangePermission,
      )}
    />
    <Route
      path="/update-check-analysis"
      element={<UpdateCheckAnalysisPage />}
    />
    <Route
      path="/genai-platform-frontend"
      element={<GenaiPlatformFrontendPage />}
    />
    <Route path="/dees" element={<DigitalEcosystemEnablementStandardsPage />} />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <ZeissFeatureFlagsProvider />
    <AppRouter>
      <VisitListener />
      <InsightsSurveyLoader />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);

const app = createApp({
  features: [convertedOptionsModule, ...convertedRootFeatures],
});

export default app.createRoot();
